/*
  Varianten-Auswahl im Warenkorb (Drawer + Warenkorb-Seite)
  Markup: snippets/cart-variant-picker.liquid (pro Position)

  Ablauf wie auf der Produktseite: Auswahl, Stückpreis, Zeilensumme und
  Gesamtbetrag wechseln sofort im Browser. Im Hintergrund wird die Position bei
  Shopify getauscht. Shopify kann die Variante einer Position nicht direkt ändern,
  darum: neue Variante mit gleicher Menge und gleichen Eigenschaften (u. a.
  PitchPrint-Projekt _pitchprint) hinzufügen, alte Position entfernen. Danach
  werden Seite und Drawer still durch den Stand von Shopify ersetzt (Preise inkl.
  Rabatte, neue Positions-Schlüssel). Die Position steht dann oben im Warenkorb
  (Shopify sortiert neue Positionen nach oben). Die PitchPrint-Vorschau wird
  dabei nicht neu geladen.

  Solange der Tausch läuft (ca. 1 s), sind die Mengenfelder gesperrt, weitere
  Klicks werden nacheinander abgearbeitet und ein Klick auf "Zur Kasse" wartet,
  bis Shopify fertig ist.
*/
(() => {
  'use strict';

  if (window.customElements.get('cart-variant-picker')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const settings = (window.themeVariables && window.themeVariables.settings) || {};
  const BUSY_CLASS = 'cart-variant-busy';

  const lines = new Map(); // Positionen mit Änderungen, Schlüssel = data-line-key beim Seitenaufbau
  let running = false;
  let checkoutPending = null; // Abschnitt, in dem während des Tauschs "Zur Kasse" geklickt wurde
  let cartTotal = null; // angezeigter Gesamtbetrag (Cent)

  async function post(path, body) {
    const response = await fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || response.statusText);
    return json;
  }

  // --- Beträge wie Shopifys money-Filter formatieren --------------------------

  function formatMoney(cents, format) {
    const match = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
    if (!match) return (cents / 100).toFixed(2);
    const amount = (precision, thousands, decimal) => {
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    const formats = {
      amount: () => amount(2, ',', '.'),
      amount_no_decimals: () => amount(0, ',', '.'),
      amount_with_comma_separator: () => amount(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => amount(0, '.', ','),
      amount_with_apostrophe_separator: () => amount(2, "'", '.'),
      amount_with_space_separator: () => amount(2, ' ', ','),
      amount_no_decimals_with_space_separator: () => amount(0, ' ', ','),
      amount_with_period_and_space_separator: () => amount(2, ' ', '.')
    };
    return format.replace(match[0], (formats[match[1]] || formats.amount)());
  }

  const money = (cents) => formatMoney(cents, settings.moneyFormat);
  const moneyWithCurrency = (cents) => formatMoney(cents, settings.moneyWithCurrencyFormat);
  const linePrice = (cents) => (settings.currencyCodeEnabled ? moneyWithCurrency(cents) : money(cents));

  // --- Zustand pro Position ----------------------------------------------------

  const pickersFor = (id) => document.querySelectorAll('cart-variant-picker[data-line-key="' + CSS.escape(id) + '"]');

  function lineState(picker) {
    const id = picker.getAttribute('data-line-key');
    if (!lines.has(id)) {
      const data = JSON.parse(picker.querySelector('script[data-variants]').textContent);
      const locked = data.locked.map((name) => name.trim());
      let properties = JSON.parse(picker.getAttribute('data-properties') || '{}');
      // Liquid gibt die Eigenschaften als Liste von [Name, Wert]-Paaren aus
      if (Array.isArray(properties)) properties = Object.fromEntries(properties);
      const variant = Number(picker.getAttribute('data-current-variant'));

      lines.set(id, {
        id,
        key: id, // aktueller Positions-Schlüssel bei Shopify
        variant, // Variante bei Shopify
        wanted: variant, // gewählte Variante
        variants: data.variants,
        lockedIndexes: data.options
          .map((name, index) => (locked.includes(name.trim().toLowerCase()) ? index : -1))
          .filter((index) => index !== -1),
        properties,
        quantity: Number(picker.getAttribute('data-quantity')) || 1,
        productId: picker.getAttribute('data-product-id'),
        sellingPlan: picker.getAttribute('data-selling-plan'),
        discounted: picker.hasAttribute('data-line-discounted')
      });
    }
    return lines.get(id);
  }

  const findVariant = (state, id) => state.variants.find((variant) => variant.id === id);

  // Ziel-Variante für einen Wert (wie in cart-variant-picker.liquid): gleicher Wert,
  // andere Optionen möglichst wie bisher, gesperrte Optionen unverändert, nur verfügbare.
  function resolve(state, current, index, value) {
    if (current.options[index] === value) return current;
    let best = null;
    let bestScore = -1;
    state.variants.forEach((variant) => {
      if (!variant.available || variant.options[index] !== value) return;
      let score = 0;
      let allowed = true;
      variant.options.forEach((option, otherIndex) => {
        if (otherIndex === index) return;
        if (option === current.options[otherIndex]) score++;
        else if (state.lockedIndexes.includes(otherIndex)) allowed = false;
      });
      if (allowed && score > bestScore) {
        best = variant;
        bestScore = score;
      }
    });
    return best;
  }

  // --- Sofort-Anzeige ----------------------------------------------------------

  function showSelection(state, variant) {
    pickersFor(state.id).forEach((picker) => {
      picker.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.getAttribute('data-option-index'));
        fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.checked = radio.value === variant.options[index];
          radio.nextElementSibling.classList.toggle('is-disabled', !resolve(state, variant, index, radio.value));
        });
      });
    });
  }

  function setPriceText(element, text) {
    // Beschriftung für Screenreader ("Angebot") behalten, nur den Betrag ersetzen
    Array.from(element.childNodes).forEach((node) => {
      if (!(node.nodeType === 1 && node.classList.contains('sr-only'))) node.remove();
    });
    element.append(text);
  }

  function showPrices(state, from, to, picker) {
    // Bei Rabatten rechnet Shopify; dann gilt erst der Stand nach dem Tausch
    if (state.discounted) return;

    pickersFor(state.id).forEach((linePicker) => {
      const lineItem = linePicker.closest('line-item');
      const salePrice = lineItem && lineItem.querySelector('price-list sale-price');
      if (salePrice) setPriceText(salePrice, linePrice(to.price));

      const row = linePicker.closest('tr');
      const lineTotal = row && row.querySelector('td.text-end');
      if (lineTotal) lineTotal.textContent = linePrice(to.price * state.quantity);
    });

    if (picker.hasAttribute('data-cart-discounted')) return;
    if (cartTotal === null) cartTotal = Number(picker.getAttribute('data-cart-total'));
    cartTotal += (to.price - from.price) * state.quantity;

    document.querySelectorAll('.cart-drawer__button-price').forEach((element) => {
      element.textContent = money(cartTotal);
    });
    document.querySelectorAll('.cart-recap span.text-lg').forEach((element) => {
      const label = element.previousElementSibling;
      if (label && label.matches('span.text-lg')) element.textContent = moneyWithCurrency(cartTotal);
    });
  }

  // --- Tausch bei Shopify (im Hintergrund, nacheinander) -----------------------

  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    const mainCart = document.querySelector('.shopify-section--main-cart');
    if (mainCart) sections.push(mainCart.id.replace(/^shopify-section-/, ''));
    return { sections: sections.join(','), sections_url: window.location.pathname };
  }

  // Neu gerenderte Abschnitte bekommen für die PitchPrint-Vorschau die Bild-Adresse,
  // die schon geladen ist (sonst neuer Zeitstempel aus line-item.liquid = neuer Download).
  function keepPreviewSources(html) {
    const loaded = {};
    document.querySelectorAll('img[data-pp-project]').forEach((img) => {
      loaded[img.getAttribute('data-pp-project')] = img.getAttribute('src');
    });
    return html.replace(/src="([^"]*\/previews\/([0-9a-f]+)_1\.jpg\?v=\d+)"/g, (match, src, projectId) =>
      loaded[projectId] ? 'src="' + loaded[projectId] + '"' : match
    );
  }

  function replaceSection(element, html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(element.id);
    if (fresh) element.innerHTML = fresh.innerHTML;
  }

  function render(cart) {
    const rendered = cart.sections || {};
    Object.keys(rendered).forEach((id) => {
      if (rendered[id]) rendered[id] = keepPreviewSources(rendered[id]);
    });
    const mainCart = document.querySelector('.shopify-section--main-cart');
    const mainCartId = mainCart && mainCart.id.replace(/^shopify-section-/, '');
    if (mainCartId && rendered[mainCartId]) replaceSection(mainCart, rendered[mainCartId]);

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } })
    );
  }

  // Nach einem Fehler: Anzeige auf den Stand von Shopify bringen
  async function refresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    const mainCart = document.querySelector('.shopify-section--main-cart');
    if (!mainCart) return;
    try {
      const id = mainCart.id.replace(/^shopify-section-/, '');
      const html = await (await fetch(window.location.pathname + '?section_id=' + encodeURIComponent(id))).text();
      replaceSection(mainCart, keepPreviewSources(html));
    } catch (error) {
      window.location.reload();
    }
  }

  // Neue Position angelegt, alte aber nicht entfernt: Neuanlage zurücknehmen
  async function rollback(added, quantity) {
    try {
      const cart = await (await fetch(root + 'cart.js')).json();
      const line = cart.items.find((lineItem) => lineItem.key === added.key);
      if (line) await post('cart/change.js', { id: line.key, quantity: Math.max(line.quantity - quantity, 0) });
    } catch (error) {}
  }

  // Die Produktseite (snippets/pitchprint-variant-design.liquid) merkt sich, für welche
  // Variante gestaltet wurde, und wählt sie beim nächsten Besuch – auf die neue umstellen.
  function updateDesignedVariant(productId, properties, variantId) {
    try {
      const store = JSON.parse(window.localStorage.getItem('pp-variant-projects') || '{}') || {};
      const saved = store[productId];
      if (!saved || !properties._pitchprint || saved.projectId !== properties._pitchprint) return;
      saved.variantId = String(variantId);
      window.localStorage.setItem('pp-variant-projects', JSON.stringify(store));
    } catch (error) {}
  }

  async function processQueue() {
    if (running) return;
    running = true;
    document.documentElement.classList.add(BUSY_CLASS);

    let state = null;
    let cart = null;
    let failed = false;
    try {
      while ((state = Array.from(lines.values()).find((line) => line.wanted !== line.variant))) {
        const target = state.wanted;
        const item = { id: target, quantity: state.quantity, properties: state.properties };
        if (state.sellingPlan) item.selling_plan = Number(state.sellingPlan);

        const added = (await post('cart/add.js', { items: [item] })).items[0];
        try {
          cart = await post('cart/change.js', Object.assign({ id: state.key, quantity: 0 }, bundledSections()));
        } catch (error) {
          await rollback(added, item.quantity);
          throw error;
        }
        state.key = added.key;
        state.variant = target;
        updateDesignedVariant(state.productId, state.properties, target);
      }
      if (cart) render(cart);
    } catch (error) {
      failed = true;
      const failedKey = state && state.key;
      await refresh();
      const picker = (failedKey && pickersFor(failedKey)[0]) || document.querySelector('cart-variant-picker');
      if (picker) picker.showError(error.message);
    } finally {
      lines.clear();
      cartTotal = null;
      running = false;
      document.documentElement.classList.remove(BUSY_CLASS);

      const pendingSection = checkoutPending;
      checkoutPending = null;
      if (pendingSection !== null) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        const scope = (pendingSection && document.getElementById(pendingSection)) || document;
        const button = scope.querySelector('[name="checkout"]');
        if (!failed && button) button.click();
      }
    }
  }

  // "Zur Kasse" während des Tauschs: warten, dann mit dem neuen Stand weiter
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('[name="checkout"]');
      if (!button || !running) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const section = button.closest('.shopify-section');
      checkoutPending = section ? section.id : '';
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    },
    true
  );

  class CartVariantPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this);
    }

    handleEvent(event) {
      const input = event.target;
      const fieldset = input.closest('fieldset[data-option-index]');
      if (!fieldset || input.type !== 'radio') return;

      this.clearError();
      const state = lineState(this);
      const current = findVariant(state, state.wanted);
      const target = current && resolve(state, current, Number(fieldset.getAttribute('data-option-index')), input.value);

      if (!target) {
        if (current) showSelection(state, current);
        this.showError(this.getAttribute('data-unavailable-message'));
        return;
      }

      state.wanted = target.id;
      showSelection(state, target);
      showPrices(state, current, target, this);
      processQueue();
    }

    clearError() {
      this.querySelectorAll('[role="alert"]').forEach((alert) => alert.remove());
    }

    showError(message) {
      const alert = document.createElement('p');
      alert.className = 'h-stack gap-2 text-xs';
      alert.setAttribute('role', 'alert');
      alert.innerHTML = '<svg width="13" height="13" fill="none" viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="6.5" fill="#BF1515"/></svg>';
      alert.append(document.createTextNode(' ' + message));
      this.append(alert);
    }
  }

  window.customElements.define('cart-variant-picker', CartVariantPicker);
})();
